import React, { useState } from 'react';
import { FiUsers, FiMicOff, FiCameraOff, FiSquare, FiX, FiMenu, FiUserX, FiCheckCircle, FiSearch, FiEdit3 } from 'react-icons/fi';

const MobileParticipantsView = ({ 
  participants, 
  activeSpeakers, 
  handleMuteViewerAudio, 
  emitSocketEvent, 
  sessionId, 
  roomCode, 
  addDebugLog, 
  handleStopViewerScreenShare, 
  handleBanParticipant,
  handleWhiteboardPermission, // 👈 Required for action menu
  allowedWhiteboardUsers = [], // 👈 Naya prop
  bannedUsers = [], 
  handleUnbanParticipant 
}) => {
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [activeTab, setActiveTab] = useState('active');
  const [searchQuery, setSearchQuery] = useState(''); // 👈 Search state

  const handleParticipantAction = (participant, action) => {
    switch (action) {
      case 'mute':
        handleMuteViewerAudio(participant.socketId);
        break;
      case 'stop_camera':
        emitSocketEvent("streamer-stop-viewer-video", {
          sessionId: sessionId || roomCode,
          targetSocketId: participant.socketId,
        });
        addDebugLog(`🛑 Force stopped camera for: ${participant.userId}`);
        break;
      case 'stop_screen':
        handleStopViewerScreenShare(participant.userId);
        break;
      case 'ban':
        const name = participant.name || participant.userName || 'this user';
        if (window.confirm(`Are you sure you want to ban ${name}?`)) {
          handleBanParticipant(participant.userId, name);
        }
        break;
      // ✏️ Draw Action cases
      case 'allow_draw':
        handleWhiteboardPermission(participant.userId, true);
        break;
      case 'revoke_draw':
        handleWhiteboardPermission(participant.userId, false);
        break;
      default:
        break;
    }
    setSelectedParticipant(null);
  };

  // 🔍 Filtering Logic
  const filteredParticipants = participants.filter(p => 
    (p.name || p.userName || p.userId || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredBanned = bannedUsers.filter(u => 
    (u.name || u.userId || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex-1 bg-gray-900 overflow-y-auto flex flex-col">
      <div className="p-4 border-b border-gray-800 shrink-0 bg-gray-900 sticky top-0 z-10">
        <h2 className="text-lg font-bold flex items-center gap-2 mb-3 text-white">
          <FiUsers className="text-blue-400" />
          <span>Participants</span>
        </h2>

        {/* TABS */}
        <div className="flex bg-gray-800 rounded-lg p-1 mb-3">
          <button
            onClick={() => setActiveTab('active')}
            className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all ${
              activeTab === 'active' 
                ? 'bg-gray-700 text-blue-400 shadow-sm' 
                : 'text-gray-400'
            }`}
          >
            Active ({participants.length})
          </button>
          <button
            onClick={() => setActiveTab('banned')}
            className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all ${
              activeTab === 'banned' 
                ? 'bg-gray-700 text-red-400 shadow-sm' 
                : 'text-gray-400'
            }`}
          >
            Banned ({bannedUsers.length})
          </button>
        </div>

        {/* 🔍 SEARCH BAR */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-gray-800 text-white border border-gray-700 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-blue-500"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute inset-y-0 right-0 pr-3 text-gray-400">
              <FiX />
            </button>
          )}
        </div>
      </div>
      
      {/* LIST CONTENT */}
      <div className="p-3 flex-1 overflow-y-auto space-y-2">
        
        {/* ACTIVE TAB */}
        {activeTab === 'active' && filteredParticipants.length === 0 && (
          <p className="text-gray-500 text-center py-8 text-sm">{searchQuery ? "No results found" : "No active participants"}</p>
        )}
        {activeTab === 'active' && filteredParticipants.map((participant) => {
          const canDraw = allowedWhiteboardUsers.includes(participant.userId);

          return (
            <div
              key={participant.userId || participant.socketId}
              className="flex items-center justify-between p-3.5 bg-gray-800 border border-gray-700 rounded-xl"
            >
              <div className="flex items-center space-x-3 flex-1 min-w-0 pr-2">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center text-gray-300 font-bold uppercase">
                    {(participant.name || participant.userName || 'U')[0]}
                  </div>
                  <div
                    className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-gray-800 ${
                      activeSpeakers.has(participant.socketId) ? "bg-green-400 animate-pulse" : participant.hasAudio ? "bg-blue-400" : "bg-gray-500"
                    }`}
                  ></div>
                </div>
                
                <div className="flex-1 min-w-0">
                  <span className="font-semibold text-sm text-gray-100 block truncate">
                    {participant.name || participant.userName || participant.userId || "User"}
                  </span>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {participant.hasAudio && <span className="text-[10px] text-blue-400 bg-blue-400/10 border border-blue-400/20 px-1.5 py-0.5 rounded">🎤 Mic</span>}
                    {participant.hasVideo && <span className="text-[10px] text-green-400 bg-green-400/10 border border-green-400/20 px-1.5 py-0.5 rounded">📹 Cam</span>}
                    {participant.isScreenSharing && <span className="text-[10px] text-yellow-400 bg-yellow-400/10 border border-yellow-400/20 px-1.5 py-0.5 rounded">🖥️ Screen</span>}
                    {canDraw && <span className="text-[10px] text-indigo-400 bg-indigo-400/10 border border-indigo-400/20 px-1.5 py-0.5 rounded flex items-center gap-1"><FiEdit3 className="w-2.5 h-2.5"/> Draw</span>}
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => setSelectedParticipant(participant)}
                className="p-2.5 bg-gray-700/50 hover:bg-gray-600 rounded-lg transition-colors text-gray-300"
              >
                <FiMenu className="h-5 w-5" />
              </button>
            </div>
          );
        })}

        {/* BANNED TAB */}
        {activeTab === 'banned' && filteredBanned.length === 0 && (
          <p className="text-gray-500 text-center py-8 text-sm">{searchQuery ? "No results found" : "No banned users"}</p>
        )}
        {activeTab === 'banned' && filteredBanned.map((user) => (
          <div 
            key={user.userId} 
            className="flex items-center justify-between p-3.5 bg-red-900/10 border border-red-900/20 rounded-xl"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center text-red-400">
                <FiUserX className="h-5 w-5" />
              </div>
              <div className="flex flex-col">
                <span className="truncate font-semibold text-gray-200 text-sm">
                  {user.name || user.userId}
                </span>
              </div>
            </div>
            <button
              onClick={() => {
                if(window.confirm(`Unban ${user.name || 'this user'}?`)) {
                  handleUnbanParticipant(user.userId);
                }
              }}
              className="px-3 py-1.5 bg-green-500/10 border border-green-500/20 text-green-400 rounded-lg text-xs font-semibold"
            >
              Unban
            </button>
          </div>
        ))}
      </div>

      {/* ACTION MENU MODAL (BOTTOM SHEET) */}
      {selectedParticipant && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-end justify-center z-[100] pb-safe">
          <div className="bg-gray-800 rounded-t-3xl w-full max-w-md border-t border-gray-700 overflow-hidden animate-slide-up">
            
            {/* Grab Handle */}
            <div className="w-full flex justify-center py-3" onClick={() => setSelectedParticipant(null)}>
              <div className="w-12 h-1.5 bg-gray-600 rounded-full"></div>
            </div>

            {/* Header */}
            <div className="px-6 pb-4 border-b border-gray-700/50">
              <h3 className="font-bold text-xl text-white">
                {selectedParticipant.name || selectedParticipant.userName || selectedParticipant.userId}
              </h3>
              <p className="text-gray-400 text-xs mt-1 uppercase tracking-wider">Manage Participant</p>
            </div>

            {/* Action Buttons */}
            <div className="p-4 space-y-2">
              
              {/* ✏️ CONDITIONAL DRAW BUTTON */}
              {allowedWhiteboardUsers.includes(selectedParticipant.userId) ? (
                <button onClick={() => handleParticipantAction(selectedParticipant, 'revoke_draw')} className="w-full flex items-center space-x-3 p-3.5 bg-orange-500/10 hover:bg-orange-500/20 rounded-xl transition-colors">
                  <FiEdit3 className="h-5 w-5 text-orange-400" />
                  <span className="text-orange-400 font-semibold">Revoke Draw Access</span>
                </button>
              ) : (
                <button onClick={() => handleParticipantAction(selectedParticipant, 'allow_draw')} className="w-full flex items-center space-x-3 p-3.5 bg-indigo-500/10 hover:bg-indigo-500/20 rounded-xl transition-colors">
                  <FiEdit3 className="h-5 w-5 text-indigo-400" />
                  <span className="text-indigo-400 font-semibold">Allow Draw Access</span>
                </button>
              )}

              {selectedParticipant.hasAudio && (
                <button onClick={() => handleParticipantAction(selectedParticipant, 'mute')} className="w-full flex items-center space-x-3 p-3.5 bg-gray-700 hover:bg-gray-600 rounded-xl transition-colors">
                  <FiMicOff className="h-5 w-5 text-gray-300" />
                  <span className="text-gray-200 font-medium">Mute Microphone</span>
                </button>
              )}

              {selectedParticipant.hasVideo && (
                <button onClick={() => handleParticipantAction(selectedParticipant, 'stop_camera')} className="w-full flex items-center space-x-3 p-3.5 bg-gray-700 hover:bg-gray-600 rounded-xl transition-colors">
                  <FiCameraOff className="h-5 w-5 text-gray-300" />
                  <span className="text-gray-200 font-medium">Stop Camera</span>
                </button>
              )}

              {selectedParticipant.isScreenSharing && (
                <button onClick={() => handleParticipantAction(selectedParticipant, 'stop_screen')} className="w-full flex items-center space-x-3 p-3.5 bg-gray-700 hover:bg-gray-600 rounded-xl transition-colors">
                  <FiSquare className="h-5 w-5 text-gray-300" />
                  <span className="text-gray-200 font-medium">Stop Screen Share</span>
                </button>
              )}

              <div className="h-px bg-gray-700/50 my-2"></div>

              <button onClick={() => handleParticipantAction(selectedParticipant, 'ban')} className="w-full flex items-center space-x-3 p-3.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-xl transition-colors">
                <FiUserX className="h-5 w-5 text-red-400" />
                <span className="text-red-400 font-bold">Ban Participant</span>
              </button>

              <button onClick={() => setSelectedParticipant(null)} className="w-full flex justify-center p-3.5 mt-2 text-gray-400 hover:text-white font-medium transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileParticipantsView;



// import React, { useState } from 'react';
// import { FiUsers, FiMicOff, FiCameraOff, FiSquare, FiX, FiMenu, FiUserX, FiCheckCircle } from 'react-icons/fi';

// const MobileParticipantsView = ({ 
//   participants, 
//   activeSpeakers, 
//   handleMuteViewerAudio, 
//   emitSocketEvent, 
//   sessionId, 
//   roomCode, 
//   addDebugLog, 
//   handleStopViewerScreenShare, 
//   handleBanParticipant,
//   bannedUsers = [], // 👈 Naya prop
//   handleUnbanParticipant // 👈 Naya prop
// }) => {
//   const [selectedParticipant, setSelectedParticipant] = useState(null);
//   const [activeTab, setActiveTab] = useState('active'); // 'active' | 'banned'

//   const handleParticipantAction = (participant, action) => {
//     switch (action) {
//       case 'mute':
//         handleMuteViewerAudio(participant.socketId);
//         break;
//       case 'stop_camera':
//         emitSocketEvent("streamer-stop-viewer-video", {
//           sessionId: sessionId || roomCode,
//           targetSocketId: participant.socketId,
//         });
//         addDebugLog(`🛑 Force stopped camera for: ${participant.userId}`);
//         break;
//       case 'stop_screen':
//         handleStopViewerScreenShare(participant.userId);
//         break;
//       case 'ban':
//         const name = participant.name || participant.userName || 'this user';
//         if (window.confirm(`Are you sure you want to ban ${name}?`)) {
//           handleBanParticipant(participant.userId, name);
//         }
//         break;
//       default:
//         break;
//     }
//     setSelectedParticipant(null);
//   };

//   return (
//     <div className="flex-1 bg-gray-900 overflow-y-auto flex flex-col">
//       <div className="p-4 border-b border-gray-700 shrink-0">
//         <h2 className="text-lg font-semibold flex items-center space-x-2">
//           <FiUsers className="h-5 w-5 text-blue-400" />
//           <span>Participants Management</span>
//         </h2>
//       </div>

//       {/* TABS */}
//       <div className="flex border-b border-gray-700 shrink-0 bg-gray-800">
//         <button
//           onClick={() => setActiveTab('active')}
//           className={`flex-1 py-3 font-medium text-sm transition-colors ${
//             activeTab === 'active' 
//               ? 'text-blue-400 border-b-2 border-blue-400' 
//               : 'text-gray-400 hover:text-gray-300'
//           }`}
//         >
//           Active ({participants.length})
//         </button>
//         <button
//           onClick={() => setActiveTab('banned')}
//           className={`flex-1 py-3 font-medium text-sm transition-colors ${
//             activeTab === 'banned' 
//               ? 'text-red-400 border-b-2 border-red-400' 
//               : 'text-gray-400 hover:text-gray-300'
//           }`}
//         >
//           Banned ({bannedUsers.length})
//         </button>
//       </div>
      
//       {/* LIST CONTENT */}
//       <div className="space-y-2 p-4 flex-1 overflow-y-auto">
        
//         {/* ACTIVE TAB */}
//         {activeTab === 'active' && participants.length === 0 && (
//           <p className="text-gray-400 text-center py-4">No active participants</p>
//         )}
//         {activeTab === 'active' && participants.map((participant) => (
//           <div
//             key={participant.userId || participant.socketId}
//             className="flex items-center justify-between p-3 bg-gray-800 rounded-lg"
//           >
//             <div className="flex items-center space-x-3 flex-1 min-w-0">
//               <div
//                 className={`w-3 h-3 rounded-full ${
//                   activeSpeakers.has(participant.socketId) ? "bg-green-400 animate-pulse" : participant.hasAudio ? "bg-blue-400" : "bg-gray-400"
//                 }`}
//               ></div>
//               <div className="flex-1 min-w-0">
//                 <span className="font-medium block truncate">
//                   {participant.name || participant.userName || participant.userId || "User"}
//                 </span>
//                 <div className="flex items-center space-x-2 mt-1">
//                   {participant.hasAudio && <span className="text-xs text-blue-400 bg-blue-400/20 px-1.5 py-0.5 rounded">🎤</span>}
//                   {participant.hasVideo && <span className="text-xs text-green-400 bg-green-400/20 px-1.5 py-0.5 rounded">📹</span>}
//                   {participant.isScreenSharing && <span className="text-xs text-purple-400 bg-purple-400/20 px-1.5 py-0.5 rounded">🖥️</span>}
//                 </div>
//               </div>
//             </div>
            
//             <button
//               onClick={() => setSelectedParticipant(participant)}
//               className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
//             >
//               <FiMenu className="h-4 w-4 text-gray-300" />
//             </button>
//           </div>
//         ))}

//         {/* BANNED TAB */}
//         {activeTab === 'banned' && bannedUsers.length === 0 && (
//           <p className="text-gray-400 text-center py-4">No banned users</p>
//         )}
//         {activeTab === 'banned' && bannedUsers.map((user) => (
//           <div 
//             key={user.userId} 
//             className="flex items-center justify-between p-3 bg-red-900/10 border border-red-900/30 rounded-lg"
//           >
//             <div className="flex items-center space-x-3">
//               <FiUserX className="h-5 w-5 text-red-400" />
//               <span className="truncate font-medium text-gray-300">
//                 {user.name || user.userId}
//               </span>
//             </div>
//             <button
//               onClick={() => {
//                 if(window.confirm(`Unban ${user.name || 'this user'}?`)) {
//                   handleUnbanParticipant(user.userId);
//                 }
//               }}
//               className="p-2 bg-green-600/20 text-green-400 rounded-lg hover:bg-green-600/30 transition-colors"
//             >
//               <FiCheckCircle className="h-5 w-5" />
//             </button>
//           </div>
//         ))}
//       </div>

//       {/* Action Menu Modal (Same as before) */}
//       {selectedParticipant && (
//         <div className="fixed inset-0 bg-black/70 flex items-end justify-center z-50 p-4">
//           <div className="bg-gray-800 rounded-2xl w-full max-w-md">
//             {/* Header */}
//             <div className="p-4 border-b border-gray-700">
//               <div className="flex items-center justify-between">
//                 <h3 className="font-semibold text-lg">Manage Participant</h3>
//                 <button onClick={() => setSelectedParticipant(null)} className="p-2 text-gray-400">
//                   <FiX className="h-5 w-5" />
//                 </button>
//               </div>
//               <p className="text-gray-300 text-sm mt-1">
//                 {selectedParticipant.name || selectedParticipant.userName || selectedParticipant.userId}
//               </p>
//             </div>

//             {/* Action Buttons */}
//             <div className="p-4 space-y-2">
//               {selectedParticipant.hasAudio && (
//                 <button onClick={() => handleParticipantAction(selectedParticipant, 'mute')} className="w-full flex items-center space-x-3 p-3 bg-red-600/20 hover:bg-red-600/30 rounded-lg transition-colors">
//                   <FiMicOff className="h-5 w-5 text-red-400" />
//                   <span className="text-red-400 font-medium">Mute Audio</span>
//                 </button>
//               )}

//               {selectedParticipant.hasVideo && (
//                 <button onClick={() => handleParticipantAction(selectedParticipant, 'stop_camera')} className="w-full flex items-center space-x-3 p-3 bg-red-600/20 hover:bg-red-600/30 rounded-lg transition-colors">
//                   <FiCameraOff className="h-5 w-5 text-red-400" />
//                   <span className="text-red-400 font-medium">Stop Camera</span>
//                 </button>
//               )}

//               {selectedParticipant.isScreenSharing && (
//                 <button onClick={() => handleParticipantAction(selectedParticipant, 'stop_screen')} className="w-full flex items-center space-x-3 p-3 bg-red-600/20 hover:bg-red-600/30 rounded-lg transition-colors">
//                   <FiSquare className="h-5 w-5 text-red-400" />
//                   <span className="text-red-400 font-medium">Stop Screen Share</span>
//                 </button>
//               )}

//               <button onClick={() => handleParticipantAction(selectedParticipant, 'ban')} className="w-full flex items-center space-x-3 p-3 bg-red-900/50 hover:bg-red-900/80 border border-red-700/50 rounded-lg transition-colors mt-4">
//                 <FiUserX className="h-5 w-5 text-red-500" />
//                 <span className="text-red-500 font-bold">Ban Participant</span>
//               </button>

//               <button onClick={() => setSelectedParticipant(null)} className="w-full flex items-center space-x-3 p-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors mt-2">
//                 <FiX className="h-5 w-5 text-gray-400" />
//                 <span className="text-gray-400 font-medium">Cancel</span>
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default MobileParticipantsView;
